<template>
  <div class="workspace-intervenants-tab">
    <!-- Search Toolbar -->
    <div v-if="showSearch" class="workspace-toolbar">
      <div class="search-box">
        <span class="search-icon">🔍</span>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('workspace.search')"
          class="search-input"
        />
      </div>
    </div>

    <!-- Advanced Filters Panel (only shown when showFilters is true) -->
    <Transition name="slide-down">
      <div v-if="showFilters && showAdvancedFilters" class="filter-panel">
        <div class="filter-panel-header">
          <h3>{{ $t('workspace.filters.title') }}</h3>
          <button v-if="hasActiveFilters" class="btn-text" @click="clearFilters">
            {{ $t('workspace.filters.clearAll') }}
          </button>
        </div>

        <div class="filter-grid">
          <!-- Type Filter -->
          <div class="filter-group">
            <label>{{ $t('workspace.filters.type') }}</label>
            <select v-model="filters.type" class="filter-select">
              <option :value="null">{{ $t('workspace.filters.allTypes') }}</option>
              <option value="ARCHITECT">{{ $t('intervenants.types.ARCHITECT') }}</option>
              <option value="CONTRACTOR">{{ $t('intervenants.types.CONTRACTOR') }}</option>
              <option value="ENGINEER">{{ $t('intervenants.types.ENGINEER') }}</option>
              <option value="CONSULTANT">{{ $t('intervenants.types.CONSULTANT') }}</option>
              <option value="SUPPLIER">{{ $t('intervenants.types.SUPPLIER') }}</option>
              <option value="OTHER">{{ $t('intervenants.types.OTHER') }}</option>
            </select>
          </div>

          <!-- Task Count Filter -->
          <div class="filter-group">
            <label>{{ $t('workspace.filters.taskCount') }}</label>
            <select v-model="filters.taskCount" class="filter-select">
              <option :value="null">{{ $t('workspace.filters.allTaskCounts') }}</option>
              <option value="0">{{ $t('workspace.filters.noTasks') }}</option>
              <option value="1+">{{ $t('workspace.filters.hasTasks') }}</option>
            </select>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Intervenants Data Grid -->
    <div class="data-grid">
      <AppDataGrid
        :row-data="intervenantGridData"
        :column-defs="intervenantColumnDefs"
        :actions="intervenantActions"
        :pagination="true"
        :page-size="10"
        :page-size-options="itemsPerPageOptions"
        :suppress-pagination-panel="false"
        :show-search="false"
        grid-class="intervenants-grid"
        grid-style="width: 100%;"
        dom-layout="autoHeight"
        :default-col-def="{ wrapText: false, autoHeight: false }"
        @row-clicked="handleIntervenantRowClick"
        @action-clicked="handleIntervenantAction"
      />
    </div>

    <!-- Budget Report Modal -->
    <Modal
      v-if="showBudgetModal"
      :show="showBudgetModal"
      @close="closeBudgetModal"
      size="large"
      :title="$t('intervenants.budgetReport')"
    >
      <div v-if="loadingBudgetReport" class="budget-loading">
        <div class="loading-spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>
      <div v-else-if="budgetReportData?.intervenantBudgetReports?.length" class="budget-modal-content">
        <IntervenantBudgetCard
          v-for="intervenant in budgetReportData.intervenantBudgetReports"
          :key="intervenant.intervenantId"
          :intervenant="intervenant"
        />
      </div>
      <div v-else class="no-budget-data">
        <p>{{ $t('intervenants.noBudgetData') }}</p>
      </div>
    </Modal>

    <!-- Delete Confirmation Modal - Intervenant -->
    <ConfirmModal
      v-if="showDeleteConfirm"
      :isVisible="showDeleteConfirm"
      :title="$t('intervenants.deleteIntervenant')"
      :message="$t('workspace.confirmDelete.intervenant')"
      :details="deleteMessage"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      type="danger"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import { usePermissions } from '@/composables/usePermissions'
import PermissionGuard from '@/components/common/PermissionGuard.vue'
import AppDataGrid from '@/components/base/AppDataGrid.vue'
import Modal from '@/components/base/Modal.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import IntervenantBudgetCard from '../../reports/IntervenantBudgetCard.vue'
import intervenantsService from '@/services/intervenants.service'
import reportService from '@/services/report.service.index'
import { getAvatarColor, getInitials } from '@/utils/uiHelpers'

interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: string
  email: string
  phone?: string
  address?: string
  representsFirm?: boolean
  firmName?: string
}

interface Task {
  id?: number
  projectId?: number
  name: string
  amount: number
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD'
  intervenantId?: number
  phaseId?: number
}

const props = defineProps<{
  projectId: number
  intervenants: Intervenant[]
  tasks: Task[]
  // Configuration options
  showSearch?: boolean
  showFilters?: boolean
  // Optional pre-filtered intervenants (for phase-specific filtering)
  filteredIntervenants?: Intervenant[]
  // Optional phase filtering context
  phaseId?: number
}>()

const emit = defineEmits<{
  intervenantSelected: [intervenant: Intervenant]
  intervenantAssigned: []
  intervenantUnassigned: []
}>()

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// Provide default values for optional props
const showSearch = computed(() => props.showSearch !== false) // Default to true
const showFilters = computed(() => props.showFilters === true) // Default to false

// State
const selectedIntervenant = ref<Intervenant | null>(null)
const searchQuery = ref('')
const showAdvancedFilters = ref(false)

// Filter state
const filters = ref({
  type: null as string | null,
  taskCount: null as string | null
})

// Pagination configuration for AppDataGrid
const itemsPerPageOptions = [5, 10, 25, 50, 100]

// Modals
const showBudgetModal = ref(false)
const showDeleteConfirm = ref(false)
const budgetReportData = ref<any | null>(null)
const loadingBudgetReport = ref(false)
const deleteIntervenantId = ref<number | null>(null)
const deleteMessage = ref('')

// Computed - Get intervenants to display (either filtered or all)
const baseIntervenants = computed(() => {
  if (props.filteredIntervenants) {
    return props.filteredIntervenants
  }
  
  // If phaseId is provided, filter intervenants by those who have tasks in that phase
  if (props.phaseId !== undefined) {
    const intervenantIds = new Set(
      props.tasks
        .filter((task: Task) => task.phaseId === props.phaseId)
        .map((task: Task) => task.intervenantId)
        .filter((id: number | undefined) => id !== undefined && id !== null)
    )
    
    return props.intervenants.filter((intervenant: Intervenant) => 
      intervenantIds.has(intervenant.id!)
    )
  }
  
  return props.intervenants
})

// Computed - Apply search and advanced filters
const searchAndFilteredIntervenants = computed(() => {
  let result = [...baseIntervenants.value]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((intervenant: Intervenant) =>
      intervenant.name?.toLowerCase().includes(query) ||
      intervenant.email?.toLowerCase().includes(query) ||
      (intervenant.phone && intervenant.phone.includes(query))
    )
  }

  // Apply advanced filters (only if showFilters is true)
  if (showFilters.value) {
    // Type filter
    if (filters.value.type) {
      result = result.filter((intervenant: Intervenant) => intervenant.type === filters.value.type)
    }

    // Task count filter
    if (filters.value.taskCount === '0') {
      result = result.filter((intervenant: Intervenant) => 
        getIntervenantTaskCount(intervenant.id!) === 0
      )
    } else if (filters.value.taskCount === '1+') {
      result = result.filter((intervenant: Intervenant) => 
        getIntervenantTaskCount(intervenant.id!) > 0
      )
    }
  }

  return result
})

// Computed - Check if any filters are active
const hasActiveFilters = computed(() => {
  return filters.value.type || filters.value.taskCount
})

// Methods
const selectIntervenant = (intervenant: Intervenant) => {
  selectedIntervenant.value = intervenant
  emit('intervenantSelected', intervenant)
}

const getIntervenantTaskCount = (intervenantId: number) => {
  let taskFilter = props.tasks.filter((t: Task) => t.intervenantId === intervenantId)
  
  // If phaseId is provided, only count tasks for that phase
  if (props.phaseId !== undefined) {
    taskFilter = taskFilter.filter((t: Task) => t.phaseId === props.phaseId)
  }
  
  return taskFilter.length
}

// Budget Report
const viewIntervenantBudget = async (intervenant: Intervenant) => {
  try {
    loadingBudgetReport.value = true
    showBudgetModal.value = true
    
    // Fetch budget report for the project
    const report = await reportService.getBudgetConsumptionReport(props.projectId)
    
    // Filter to show only the selected intervenant's budget report
    budgetReportData.value = {
      intervenantBudgetReports: report.intervenantBudgetReports?.filter(
        (item: any) => item.intervenantId === intervenant.id
      ) || []
    }
  } catch (error) {
    console.error('Error fetching budget report:', error)
    notifyError(t('intervenants.errors.failedToLoad'))
    showBudgetModal.value = false
  } finally {
    loadingBudgetReport.value = false
  }
}

const closeBudgetModal = () => {
  showBudgetModal.value = false
  budgetReportData.value = null
}

// Edit Intervenant
const editIntervenant = (intervenant: Intervenant) => {
  // Emit to parent to handle editing
  emit('intervenantSelected', intervenant)
}

// Delete Intervenant
const deleteIntervenant = (id: number) => {
  const intervenant = props.intervenants.find((i: Intervenant) => i.id === id)
  deleteIntervenantId.value = id
  deleteMessage.value = intervenant?.name || ''
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  if (deleteIntervenantId.value === null) return
  
  try {
    await intervenantsService.deleteIntervenant(
      props.projectId.toString(), 
      deleteIntervenantId.value.toString()
    )
    notifySuccess(t('workspace.success.intervenantDeleted'))
    emit('intervenantUnassigned') // Refresh data
  } catch (error) {
    notifyError(t('workspace.errors.intervenantDeleteFailed'))
  } finally {
    cancelDelete()
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
  deleteIntervenantId.value = null
  deleteMessage.value = ''
}

const clearFilters = () => {
  filters.value = {
    type: null,
    taskCount: null
  }
}

// Grid Configuration
const { hasPermissionForProject, PermissionType } = usePermissions()

// Custom cell renderers for intervenants grid
const intervenantCellRenderers: Record<string, (params: any) => string> = {
  name: (params: any) => {
    const bgColor = getAvatarColor(params.data.nameRaw)
    const initials = getInitials(params.data.nameRaw)
    const firmBadge = params.data.representsFirm ? `
      <span style="
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        font-size: 14px;
        background-color: #dbeafe;
        border-radius: 6px;
        color: #1e40af;
        margin-left: 8px;
      " title="${t('intervenants.firmRepresentative')}">🏢</span>
    ` : ''
    return `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: ${bgColor};
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 12px;
          flex-shrink: 0;
        ">${initials}</div>
        <strong style="font-weight: 600; color: #1f2937;">${params.value}</strong>
        ${firmBadge}
      </div>
    `
  },
  
  type: (params: any) => {
    if (!params.value) return '-'
    return `
      <span style="
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
        background-color: #dbeafe;
        color: #1e40af;
        display: inline-block;
      ">${params.value}</span>
    `
  },
  
  email: (params: any) => {
    if (!params.value) return '-'
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  phone: (params: any) => {
    if (!params.value) return '-'
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  tasks: (params: any) => {
    const count = params.value || 0
    return `
      <span style="
        font-weight: 600;
        color: #374151;
        font-size: 14px;
      ">${count}</span>
    `
  }
}

// Prepare intervenant data for grid
const intervenantGridData = computed(() => {
  return searchAndFilteredIntervenants.value.map((intervenant: Intervenant) => ({
    id: intervenant.id,
    name: intervenant.name,
    nameRaw: intervenant.name,
    type: intervenant.type ? t(`intervenants.types.${intervenant.type}`) : '-',
    typeRaw: intervenant.type,
    email: intervenant.email,
    phone: intervenant.phone || '-',
    tasks: getIntervenantTaskCount(intervenant.id!),
    representsFirm: intervenant.representsFirm,
    _originalData: intervenant
  }))
})

// Column definitions for intervenants grid
const intervenantColumnDefs = computed(() => [
  {
    field: 'name',
    headerName: t('workspace.intervenants.name'),
    cellRenderer: intervenantCellRenderers.name,
    minWidth: 200,
    flex: 1,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'type',
    headerName: t('workspace.intervenants.type'),
    cellRenderer: intervenantCellRenderers.type,
    width: 180,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'email',
    headerName: t('workspace.intervenants.email'),
    cellRenderer: intervenantCellRenderers.email,
    width: 220,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'phone',
    headerName: t('workspace.intervenants.phone'),
    cellRenderer: intervenantCellRenderers.phone,
    width: 150,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'tasks',
    headerName: t('workspace.intervenants.tasks'),
    cellRenderer: intervenantCellRenderers.tasks,
    width: 120,
    sortable: true,
    wrapText: false,
    autoHeight: false
  }
])

// Action definitions for intervenants grid (will be filtered per row based on condition)
const intervenantActions = computed(() => {
  const actions = []
  
  // View Budget Report button
  if (hasPermissionForProject(PermissionType.VIEW_REPORTS, props.projectId).value) {
    actions.push({
      name: 'viewBudget',
      label: t('intervenants.viewBudgetReport'),
      icon: 'fas fa-chart-bar',
      class: 'view-btn',
      handler: (data: any) => viewIntervenantBudget(data._originalData)
    })
  }
  
  // Edit button (hidden for firm representatives)
  if (hasPermissionForProject(PermissionType.UPDATE_INTERVENANT, props.projectId).value) {
    actions.push({
      name: 'edit',
      label: t('common.edit'),
      icon: 'fas fa-edit',
      class: 'edit-btn',
      handler: (data: any) => editIntervenant(data._originalData),
      condition: (data: any) => !data.representsFirm
    })
  }
  
  // Delete button (hidden for firm representatives)
  if (hasPermissionForProject(PermissionType.DELETE_INTERVENANT, props.projectId).value) {
    actions.push({
      name: 'delete',
      label: t('common.delete'),
      icon: 'fas fa-trash',
      class: 'delete-btn',
      handler: (data: any) => deleteIntervenant(data.id),
      condition: (data: any) => !data.representsFirm
    })
  }
  
  return actions
})

// Handle intervenant grid actions
const handleIntervenantAction = (action: any, data: any) => {
  action.handler(data)
}

// Handle intervenant row clicks
const handleIntervenantRowClick = (event: any) => {
  selectIntervenant(event.data._originalData)
}
</script>

<style scoped>
.workspace-intervenants-tab {
  padding: 0;
}

/* Search Toolbar */
.workspace-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-500);
  font-size: 0.875rem;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

/* Filter Panel */
.filter-panel {
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  padding: 1rem;
}

.filter-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.filter-panel-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.btn-text {
  background: none;
  border: none;
  color: var(--color-primary);
  cursor: pointer;
  font-size: 0.875rem;
  text-decoration: underline;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--neutral-700);
}

.filter-select {
  padding: 0.5rem;
  border: 1px solid var(--neutral-300);
  border-radius: 4px;
  font-size: 0.875rem;
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 2px var(--primary-100);
}

/* Data Grid */
.data-grid {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.workspace-table thead {
  background: var(--neutral-50);
  border-bottom: 1px solid var(--neutral-200);
}

.workspace-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--neutral-700);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.workspace-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--neutral-200);
  color: var(--neutral-900);
}

.workspace-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s;
}

.workspace-table tbody tr:hover {
  background: var(--neutral-50);
}

.workspace-table tbody tr.selected {
  background: var(--primary-50);
}

.workspace-table tbody tr:last-child td {
  border-bottom: none;
}

.cell-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.avatar-small {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.type-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--primary-100);
  color: var(--primary-700);
  display: inline-block;
}

.task-count {
  font-weight: 600;
  color: var(--neutral-700);
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-icon-small {
  width: 2rem;
  height: 2rem;
  border: none;
  background: var(--neutral-100);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--neutral-600);
}

.btn-icon-small:hover {
  background: var(--neutral-200);
  color: var(--neutral-800);
}

.btn-icon-small.btn-danger {
  color: var(--error-600);
}

.btn-icon-small.btn-danger:hover {
  background: var(--error-100);
  color: var(--error-700);
}

.firm-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  font-size: 1rem;
  background: var(--primary-100);
  border-radius: 6px;
  color: var(--primary-700);
  cursor: help;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--neutral-500);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  color: var(--neutral-500);
}

/* Budget Modal */
.budget-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid var(--neutral-200);
  border-top: 2px solid var(--primary-500);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.budget-modal-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.no-budget-data {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--neutral-500);
}

/* Transitions */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
  .workspace-table {
    font-size: 0.875rem;
  }

  .workspace-table th,
  .workspace-table td {
    padding: 0.75rem 0.5rem;
  }

  .cell-content {
    gap: 0.5rem;
  }

  .avatar-small {
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.6875rem;
  }

  .action-buttons {
    gap: 0.25rem;
  }

  .btn-icon-small {
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.75rem;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }
}
</style>